import { filter, isEqual, map, toNumber } from 'lodash';
import { DateTimeFormatter, LocalDate } from '@js-joda/core';
import { IconChange, IconDollar, IconHide } from '@americanexpress/dls-react';
import DateUtils from 'src/utils/date';
import BWDataService from './bw-data.service';
import { CategoryModel } from 'src/types/category.model';
import { Direction } from 'src/types/sort-direction.enum';
import { TransactionType } from 'src/types/transaction-type.enum';
import { TransactionModel, TransactionSummary } from 'src/types/transaction.model';
import LiteralsService from './literals/LiteralsService';
import { PaginatedTransactionsModel } from 'src/types/paginated-transactions.model';

export enum Frequency {
    TYPICAL = 'typical',
    ONETIME = 'one-time',
    IGNORE = 'ignore'
}

export const frequencies = [
    {
        id: Frequency.TYPICAL,
        name: LiteralsService.getLiteral('TransactionsTable.frequency.typical'),
        icon: IconChange
    },
    {
        id: Frequency.ONETIME,
        name: LiteralsService.getLiteral('TransactionsTable.frequency.oneTime'),
        icon: IconDollar
    },
    {
        id: Frequency.IGNORE,
        name: LiteralsService.getLiteral('TransactionsTable.frequency.ignore'),
        icon: IconHide
    }
];

export default class TransactionService extends BWDataService {
    public static async getTransactionsForCategory(
        startDate: LocalDate,
        endDate: LocalDate,
        category: string
    ): Promise<TransactionModel[]> {
        const transactions: TransactionModel[] = await this.get(
            '/transactions/user/details/' +
                category +
                '?startDate=' +
                startDate.format(DateTimeFormatter.ISO_LOCAL_DATE) +
                '&endDate=' +
                endDate.format(DateTimeFormatter.ISO_LOCAL_DATE)
        );

        transactions?.forEach((transaction) => {
            transaction.date = DateUtils.dateify(transaction.date);
        });

        return transactions;
    }

    public static async getTransactionSummaries(
        startDate: LocalDate,
        endDate: LocalDate
    ): Promise<TransactionSummary[]> {
        return await this.get(
            '/transactions/summary' +
                '?startDate=' +
                startDate.format(DateTimeFormatter.ISO_LOCAL_DATE) +
                '&endDate=' +
                endDate.format(DateTimeFormatter.ISO_LOCAL_DATE)
        );
    }

    /** deprecated: use getFilteredTransactions instead */
    public static async getTransactions(startDate: LocalDate, endDate: LocalDate): Promise<TransactionModel[]> {
        const url =
            '/transactions/user/details/none' +
            (startDate ? '?startDate=' + startDate.format(DateTimeFormatter.ISO_LOCAL_DATE) : '') +
            (endDate ? '&endDate=' + endDate.format(DateTimeFormatter.ISO_LOCAL_DATE) : '');
        const transactions: TransactionModel[] = await this.get(url);

        transactions?.forEach((transaction) => {
            transaction.date = DateUtils.dateify(transaction.date);
        });
        return transactions;
    }

    public static async getFilteredTransactions(
        startDate: LocalDate,
        endDate: LocalDate,
        search: string,
        account: string,
        type: TransactionType,
        group: string,
        income: string,
        sort: string,
        isAscending: boolean,
        page: number,
        showHidden: boolean
    ): Promise<PaginatedTransactionsModel> {
        const url: string =
            `/transactions/filter?sort=${sort}&ascending=${isAscending}&page=${page}&showHidden=${showHidden}` +
            (startDate ? `&startDate=${startDate.format(DateTimeFormatter.ISO_LOCAL_DATE)}` : '') +
            (endDate ? `&endDate=${endDate.format(DateTimeFormatter.ISO_LOCAL_DATE)}` : '') +
            (search ? `&search=${search}` : '') +
            (account ? `&accountId=${account}` : '') +
            (type ? `&type=${type}` : '') +
            (group ? `&group=${group}` : '') +
            (income ? `&income=${income}` : '');
        const results: PaginatedTransactionsModel = await this.get(url);
        const transactions: TransactionModel[] = results?.transactions;

        transactions?.forEach((transaction) => {
            transaction.date = DateUtils.dateify(transaction.date);
        });
        this.addFrequencies(transactions);
        return { ...results, transactions };
    }

    public static async getTransaction(id: number): Promise<TransactionModel> {
        const response: TransactionModel = await this.get(`/transactions/${id}`);
        return this.handleTransactionResponse(response);
    }

    public static async updateTransaction(transaction: TransactionModel): Promise<TransactionModel> {
        const response: TransactionModel = await this.put(`/transactions/${transaction.id}`, {
            groupUid: transaction.groupUid,
            nickname:
                transaction.nickName && transaction.nickName !== transaction.originalName ? transaction.nickName : null,
            incomeUid: transaction.incomeUid || null,
            ignore: transaction.ignore,
            ignoreInFuture: transaction.ignoreInFuture
        });

        if (response.date) {
            response.date = DateUtils.dateify(response.date);
        }
        return response;
    }

    public static async updateTransactions(transactions: TransactionModel[]): Promise<void> {
        return await this.put(
            '/transactions/batch-update',
            map(transactions, (t: TransactionModel) => ({
                transactionId: t.id,
                update: {
                    groupUid: t.groupUid,
                    nickname: t.nickName && t.nickName !== t.originalName ? t.nickName : null,
                    incomeUid: t.incomeUid || null,
                    ignore: t.ignore,
                    ignoreInFuture: t.ignoreInFuture
                }
            }))
        );
    }

    public static async getTransactionsForReview(): Promise<TransactionModel[]> {
        return await this.get('/transactions/review');
    }

    public static async getFilteredTransactionsForReview(
        sort: string,
        isAscending: boolean,
        search: string,
        page: number,
        showHidden: boolean = true
    ): Promise<PaginatedTransactionsModel> {
        const url: string =
            `/transactions/review/filter?sort=${sort}&page=${page}&ascending=${isAscending}&showHidden=${showHidden}` +
            (search ? `&search=${search}` : '');

        const results: PaginatedTransactionsModel = await this.get(url);
        const transactions: TransactionModel[] = results?.transactions;

        transactions?.forEach((transaction) => {
            transaction.date = DateUtils.dateify(transaction.date);
        });
        this.addFrequencies(transactions);
        return { ...results, transactions };
    }

    public static async markTransactionReviewed(transactionId: number): Promise<void> {
        return await this.delete('/transactions/review/' + String(transactionId));
    }

    public static async markTransactionsReviewed(transactionIds: number[]): Promise<void> {
        return await this.post('/transactions/review/batch-delete', transactionIds);
    }

    public static async updateTransactionsGroup(transactions: TransactionModel[], groupUid: string): Promise<void> {
        await this.put('/transactions/group', {
            transactionIds: map(transactions, (t: TransactionModel) => t.id),
            groupUid
        });
    }

    public static async updateTransactionsIncomeSource(
        transactions: TransactionModel[],
        incomeSourceId: string
    ): Promise<void> {
        await this.put('/transactions/income', {
            transactionIds: transactions.map((transaction) => transaction.id),
            incomeUid: incomeSourceId
        });
    }

    public static async updateTransactionsIgnore(
        transactions: TransactionModel[],
        ignore: boolean,
        ignoreInFuture: boolean
    ): Promise<void> {
        await this.put('/transactions/ignore', {
            transactionIds: transactions.map((transaction) => transaction.id),
            ignore,
            ignoreInFuture
        });
    }

    /** Filters the transactions by accountId
     * @param transactions - array of transactions.
     * @param accountId - accountId.
     * @returns array of filtered transactions.
     */
    public static filterTransactionsByAccountId(
        transactions: TransactionModel[],
        accountId: string
    ): TransactionModel[] {
        if (accountId && Number(accountId) !== -1) {
            return filter(transactions, (transaction) => {
                return transaction.accountId === Number(accountId);
            });
        }

        return transactions;
    }

    /**
     * Filters the transactions by the search input of the user
     * @param transactions - array of transactions.
     * @param search - search input.
     * @returns array of filtered transactions.
     */
    public static filterTransactionsBySearch(transactions: TransactionModel[], search: string): TransactionModel[] {
        if (search?.length) {
            // if the search is a number, it could be looking for an exact id
            const numSearch = toNumber(search);
            if (!isNaN(numSearch)) {
                return filter(transactions, (transaction) => transaction.id === numSearch);
            } else {
                // not a number search, compare ignoring case
                return filter(transactions, (transaction) =>
                    transaction.displayName.toUpperCase().includes(search.toUpperCase())
                );
            }
        }

        return transactions;
    }

    /** Filters for a specific transaction by its id
     * @param transactions - array of transactions.
     * @param selections - transaction id.
     * @returns array of filtered transactions.
     */
    public static filterTransactionsById(transactions: TransactionModel[], selections: string): TransactionModel[] {
        if (selections?.length) {
            return filter(transactions, (transaction) => {
                return String(transaction.id) === selections;
            });
        }

        return transactions;
    }

    /** Filters transactions by the required Transaction Type
     * @param transactions - array of transactions.
     * @param type - transaction type.
     * @returns array of filtered transactions.
     */
    public static filterTransactionsByType(
        transactions: TransactionModel[],
        type: TransactionType
    ): TransactionModel[] {
        if (type?.length) {
            return filter(transactions, (transaction) => {
                return transaction.type === type;
            });
        }

        return transactions;
    }

    /** Filters transactions by the required expense category
     * @param transactions - array of transactions.
     * @param categories - expense categories.
     * @param groupUid - expense groupUid.
     * @returns array of filtered transactions.
     */
    public static filterTransactionsByGroupUid(
        transactions: TransactionModel[],
        categories: CategoryModel[],
        groupUid: string
    ): TransactionModel[] {
        if (groupUid?.length) {
            const groupUids: string[] = map(categories, (category) => {
                if (category.id === groupUid || category.parentId === groupUid) {
                    return category.id;
                }
            });
            return filter(transactions, (transaction) => {
                return (
                    transaction.type === TransactionType.TRANSACTION_EXPENSE && groupUids.includes(transaction.groupUid)
                );
            });
        }

        return transactions;
    }

    /** Filters transactions by the required income source
     * @param transactions - array of transactions.
     * @param incomeSourceUid - income source uid.
     * @returns array of filtered transactions.
     */
    public static filterTransactionsByIncomeSourceUid(
        transactions: TransactionModel[],
        incomeSourceUid: string
    ): TransactionModel[] {
        if (incomeSourceUid?.length) {
            return filter(transactions, (transaction) => {
                return (
                    transaction.type === TransactionType.TRANSACTION_INCOME && transaction.incomeUid === incomeSourceUid
                );
            });
        }

        return transactions;
    }

    /** Sorts transactions by date
     * @param transactions - array of transactions.
     * @param direction - the direction to sort the transactions.
     * @returns array of sorted transactions.
     */
    public static sortTransactionsByDate(transactions: TransactionModel[], direction: string): TransactionModel[] {
        const sortedTransactions: TransactionModel[] = transactions
            ?.slice()
            .sort((a: TransactionModel, b: TransactionModel) => {
                if (isEqual(a.date, b.date)) {
                    return a.name.toLowerCase() < b.name.toLowerCase() ? 1 : -1;
                }
                return a.date < b.date ? 1 : -1;
            });

        if (direction === Direction.ASCENDING) {
            sortedTransactions.reverse();
        }

        return sortedTransactions;
    }

    /** Retrieves a paginated array of transactions
     * @param transactions - array of transactions.
     * @param pageSize - the number of transactions per page.
     * @param pageNumber - the current page number.
     * @returns array of paginated transactions, and the total number of pages for all transactions.
     */
    public static getPaginatedTransactions(
        transactions: TransactionModel[],
        pageSize: number = 50,
        pageNumber: number
    ): { paginatedTransactions: TransactionModel[]; pageCount: number } {
        const pageCount: number = Math.ceil(transactions?.length / pageSize);
        const startItemIndex: number = (pageNumber - 1) * pageSize;
        const endItemIndex: number = Math.min(startItemIndex + pageSize, transactions?.length);
        const paginatedTransactions: TransactionModel[] = transactions?.slice(startItemIndex, endItemIndex);

        return {
            paginatedTransactions,
            pageCount
        };
    }

    public static getTransactionFrequency(transaction: TransactionModel): string {
        if (transaction.ignore) {
            return Frequency.IGNORE;
        } else if (transaction.ignoreInFuture) {
            return Frequency.ONETIME;
        } else {
            return Frequency.TYPICAL;
        }
    }

    public static getTransactionIgnore(frequency: string): boolean {
        return frequency === Frequency.IGNORE;
    }

    public static getTransactionIgnoreInFuture(frequency: string): boolean {
        return frequency === Frequency.ONETIME;
    }

    public static addFrequencies(transactions: TransactionModel[]): any[] {
        for (const e of transactions) this.addFrequency(e);
        return transactions;
    }

    public static addFrequency(transaction: TransactionModel): TransactionModel {
        if (transaction.ignore) transaction.frequency = Frequency.IGNORE;
        else if (transaction.ignoreInFuture) transaction.frequency = Frequency.ONETIME;
        else transaction.frequency = Frequency.TYPICAL;
        return transaction;
    }

    public static updateFrequency(transaction: TransactionModel, frequency: string): TransactionModel {
        return {
            ...transaction,
            frequency,
            ignore: this.getTransactionIgnore(frequency),
            ignoreInFuture: this.getTransactionIgnoreInFuture(frequency)
        };
    }

    public static handleTransactionResponse(response: TransactionModel): TransactionModel {
        this.addFrequency(response);
        if (response.date) {
            response.date = DateUtils.dateify(response.date);
        }
        return response;
    }
}
